import React from 'react';
import { styled } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import theme from 'theme';
import { Colors } from 'library';
import { useSidebarWidth } from 'parent-portal/navigation/LayoutSidebar';
import { Icon } from './Icon';
import { ResponsiveRow } from './ResponsiveRow';
import { Row } from './Row';
import { Stack } from './Stack';
import { Text } from './Text';

type IconTone = 'default' | 'muted' | 'outlined' | 'accent';

interface FormFooterProps {
    icon?: SvgIconComponent;
    title?: string;
    summary?: string;
    iconTone?: IconTone;
    iconColor?: string;
    iconBgColor?: string;
    message?: React.ReactNode;
    leftActions?: React.ReactNode;
    rightActions?: React.ReactNode;
}

export function FormFooter({
    icon,
    title,
    summary,
    iconTone,
    iconColor,
    iconBgColor,
    message,
    leftActions,
    rightActions,
}: FormFooterProps) {
    const sidebarWidth = useSidebarWidth();
    return (
        <>
            <Spacer />
            <Bar sidebarWidth={sidebarWidth}>
                <ResponsiveRow justify="between" align="center" gap="md" collapseAt="md">
                    <Row gap="md">
                        {leftActions}
                        {icon && (
                            <Icon icon={icon} size="md" tone={iconTone} iconColor={iconColor} bgColor={iconBgColor} />
                        )}
                        <Stack gap="none">
                            {title && <Text weight="semibold">{title}</Text>}
                            {summary && (
                                <Text size="sm" tone="secondary">
                                    {summary}
                                </Text>
                            )}
                        </Stack>
                    </Row>
                    <Row gap="md" justify="end" align="center">
                        {message}
                        {rightActions}
                    </Row>
                </ResponsiveRow>
            </Bar>
        </>
    );
}

const Bar = styled('div')<{ sidebarWidth: number }>(({ sidebarWidth }) => ({
    position: 'fixed',
    bottom: 0,
    left: sidebarWidth,
    right: 0,
    zIndex: 1100,
    backgroundColor: Colors.white,
    borderTop: `1px solid ${Colors.border}`,
    padding: '12px 32px',
}));

const Spacer = styled('div')({
    height: 88,
    [theme.breakpoints.down('md')]: {
        height: 148,
    },
});
